import React, { useState, useEffect, useRef } from 'react';
import {
  StyleSheet,
  View,
  Animated,
  Pressable,
  ScrollView,
  ActivityIndicator,
  Easing,
  Platform,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { auth } from '../../config/firebase';
import { QuestionAnswer } from '../../services/discoveryOnboardingAPI';
import { completeOnboarding as markOnboardingComplete } from '../../utils/onboardingStorage';
import { 
  PageContainer, 
  ContentContainer,
  Text, 
  Card, 
  Button,
  ChatAvatar,
  ChatBubble,
  Theme,
  OnboardingPageContainer
} from '../../components/ui';
import { 
  mentorChatService,
  MentorGatewayMessage,
} from '../../services/mentorChatService';
import { PrivateProfileService } from '../../services/privateProfileService';
import { 
  OnboardingPhase1Schema,
} from '../../utils/mentorSchemas';
import { 
  ProfileSummary,
  DailyRecommendations,
  OnboardingAnswersCollection,
  ActiveContext,
  PrivateProfile,
  MentorChatHistory,
} from '../../models/PrivateProfile';
import { errorReporting } from '../../services/errorReportingService';
import { 
  buildPhase1Messages, 
  deriveAudiencesFromAnswers, 
  getVectorRecommendations, 
  OnboardingQA 
} from '../../services/onboardingRecommendationService';

interface ProgressItem {
  title: string;
  color: string;
  progress: Animated.Value;
  completed: boolean;
  displayValue: number;
}

const CalculationScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { t } = useTranslation();
  
  // Get data from route params
  const routeParams = route.params as any;
  const questionsAndAnswers = routeParams?.questionsAndAnswers || [];
  
  // Streaming state
  const [streamingText, setStreamingText] = useState('');
  const [showProgressBars, setShowProgressBars] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isThinking, setIsThinking] = useState(true); // Start in thinking state
  const [hasReceivedFirstText, setHasReceivedFirstText] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true); // Track initial mount
  
  // Profile data for navigation
  const [profileData, setProfileData] = useState<any>(null);
  
  // Main focus data for display
  const [mainFocusData, setMainFocusData] = useState<{
    category: string;
    label: string;
    primaryGoal?: string;
  } | null>(null);
  
  // Extract user-specific info for personalized progress messages
  const onboardingQA: OnboardingQA[] = questionsAndAnswers.map((qa: QuestionAnswer) => ({
    question: qa.question,
    answer: qa.answer,
  }));

  const getUserGoal = () => {
    const goalAnswer = questionsAndAnswers.find((qa: QuestionAnswer) => {
      const q = qa.question.toLowerCase();
      const isGoal = (q.includes('goal') || q.includes('outcome')) && 
                     (q.includes('main') || q.includes('right now'));
      const isStruggle = q.includes('biggest struggle') || 
                         (q.includes('struggle') && q.includes('right now'));
      return isGoal || isStruggle;
    });
    return goalAnswer ? String(goalAnswer.answer).substring(0, 50) : null;
  };

  const getUserChallenge = () => {
    const challengeAnswer = questionsAndAnswers.find((qa: QuestionAnswer) => {
      const q = qa.question.toLowerCase();
      return (q.includes('holding') && q.includes('back')) || 
             q.includes('biggest thing') || 
             (q.includes('blocking') || q.includes('challenge'));
    });
    return challengeAnswer ? String(challengeAnswer.answer).substring(0, 50) : null;
  };

  const getUserStyle = () => {
    const styleAnswer = questionsAndAnswers.find((qa: QuestionAnswer) => 
      qa.question.toLowerCase().includes('coaching style') ||
      qa.question.toLowerCase().includes('learning style')
    );
    if (!styleAnswer) return null;
    
    // Get the answer ID
    const answerId = String(Array.isArray(styleAnswer.answer) ? styleAnswer.answer[0] : styleAnswer.answer);
    
    // Try to find the label from the answer text (if it's already a label)
    // Otherwise just return the ID with proper formatting
    const labelMap: Record<string, string> = {
      'tactics': 'Practical strategies & tactics',
      'stories': 'Personal transformation stories',
      'research': 'Research-based',
      'philosophy': 'Philosophy & wisdom',
      'mix': 'Mix of everything',
      'direct': 'Direct and challenging',
      'supportive': 'Supportive and encouraging',
      'data_driven': 'Data-driven and logical',
      'inspirational': 'Inspirational and visionary',
      'practical': 'Practical and actionable',
    };
    
    return labelMap[answerId] || answerId;
  };

  const userGoal = getUserGoal();
  const userChallenge = getUserChallenge();
  const userStyle = getUserStyle();

  const getUserAge = () => {
    const ageAnswer = questionsAndAnswers.find((qa: QuestionAnswer) => 
      qa.question.toLowerCase().includes('age')
    );
    return ageAnswer ? String(ageAnswer.answer) : null;
  };

  const getUserFocus = () => {
    const focusAnswer = questionsAndAnswers.find((qa: QuestionAnswer) => 
      qa.question.toLowerCase().includes('growth focus')
    );
    if (!focusAnswer) return null;
    
    const answerId = String(focusAnswer.answer);
    const labelMap: Record<string, string> = {
      'personal': 'Personal development & life balance',
      'business': 'Business & career advancement',
      'both': 'Personal & business growth',
      'exploring': 'Exploring options',
    };
    
    return labelMap[answerId] || answerId;
  };

  const userAge = getUserAge();
  const userFocus = getUserFocus();
  const userAudiences = deriveAudiencesFromAnswers(onboardingQA);

  // Progress bars state with personalized messages
  const [progressItems, setProgressItems] = useState<ProgressItem[]>([
    {
      title: userGoal 
        ? `Matching your goal: "${userGoal}..." with relevant books`
        : t('calculation.progressSteps.analyzingResponses'),
      color: Theme.colors.primary500,
      progress: new Animated.Value(0),
      completed: false,
      displayValue: 0,
    },
    {
      title: userChallenge
        ? `Finding solutions for: "${userChallenge}..."`
        : t('calculation.progressSteps.identifyingInsights'),
      color: Theme.colors.primary300,
      progress: new Animated.Value(0),
      completed: false,
      displayValue: 0,
    },
    {
      title: userStyle
        ? `Selecting ${userStyle.toLowerCase()} content for you`
        : t('calculation.progressSteps.creatingProfile'),
      color: Theme.colors.accent500,
      progress: new Animated.Value(0),
      completed: false,
      displayValue: 0,
    },
    {
      title: t('calculation.progressSteps.creatingPlan'),
      color: Theme.colors.accent600,
      progress: new Animated.Value(0),
      completed: false,
      displayValue: 0,
    },
  ]);
  
  const [allAnimationCompleted, setAllAnimationCompleted] = useState(false);
  const [streamingCompleted, setStreamingCompleted] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  
  // Scroll ref for auto-scrolling
  const scrollViewRef = useRef<ScrollView>(null);

  useEffect(() => {
    // Small delay to show initial loading state, then start
    const timer = setTimeout(() => {
      setIsInitializing(false);
      startOnboarding();
    }, 100);
    
    return () => {
      clearTimeout(timer);
    };
  }, []);

  // Start progress animations immediately when bars are shown
  useEffect(() => {
    if (showProgressBars) {
      animateProgress();
    }
  }, [showProgressBars]);

  // Show summary when AI processing is complete (don't wait for animations)
  useEffect(() => {
    if (profileData && streamingCompleted && !showSummary) {
      console.log('🎉 All onboarding processing complete - showing summary...');
      setTimeout(() => {
        setShowSummary(true);
      }, 500);
    }
  }, [profileData, streamingCompleted, showSummary]);

  const handleContinueToDashboard = async () => {
    // Check if user is anonymous
    const user = auth.currentUser;
    const isAnonymous = user?.isAnonymous;
    
    if (isAnonymous) {
      // If user is anonymous, navigate to authentication screen
      // Don't mark onboarding complete yet - wait until they create an account
      (navigation as any).navigate('Auth', { 
        screen: 'Register',
        params: { fromOnboarding: true }
      });
      return;
    }
    
    // Mark onboarding as complete only for authenticated users
    try {
      await markOnboardingComplete();
    } catch (error) {
      errorReporting.reportWarning('Failed to mark onboarding complete', error, {
        userId: auth.currentUser?.uid,
        screen: 'CalculationScreen',
        action: 'navigation',
      });
    }
    
    // Navigate directly to Main dashboard
    (navigation as any).reset({
      index: 0,
      routes: [{ name: 'Main' }],
    });
  };

  const startOnboarding = async () => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) {
        setError('No authenticated user');
        return;
      }

      console.log('🚀 Starting onboarding:', {
        userId,
        isAnonymous: auth.currentUser?.isAnonymous,
        questionsCount: questionsAndAnswers.length,
        audiences: userAudiences,
      });

      // Profile is already initialized by App.tsx on authentication
      // Proceed with populating it with onboarding data
      const success = await completeOnboardingTask(userId);
      
      if (!success) {
        throw new Error('Failed to complete onboarding task with AI');
      }
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      
      console.error('❌ startOnboarding failed:', {
        error: errorObj.message,
        errorName: errorObj.name,
        userId: auth.currentUser?.uid,
      });

      errorReporting.reportError(errorObj, {
        userId: auth.currentUser?.uid,
        screen: 'CalculationScreen',
        action: 'startOnboarding',
        metadata: {
          isCritical: true,
          questionsAnswered: questionsAndAnswers.length,
          audiences: userAudiences,
          errorMessage: errorObj.message,
          errorName: errorObj.name,
          errorStack: errorObj.stack,
          // Auth diagnostics
          isAuthenticated: !!auth.currentUser,
          isAnonymous: auth.currentUser?.isAnonymous,
          authUserId: auth.currentUser?.uid,
          // Network diagnostics
          onLine: typeof navigator !== 'undefined' ? navigator.onLine : 'unknown',
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
          platform: Platform.OS,
        }
      });
      setError(errorObj.message || 'Failed to complete onboarding');
    }
  };

  const completeOnboardingTask = async (userId: string): Promise<boolean> => {
    let currentPhase = 'initialization';
    
    try {
      // Get existing profile (we know it exists after initialization)
      const profileResult = await PrivateProfileService.getProfile(userId);
      if (!profileResult.success || !profileResult.data) {
        throw new Error(`Profile not found after initialization: ${profileResult.error}`);
      }
      
      const userProfile: PrivateProfile = profileResult.data;

      // Persist explicit audiences for downstream filtering (empty array = general/unknown; do NOT store "general").
      await PrivateProfileService.updateProfile(userId, { audiences: userAudiences });

      // Extract and EMPHASIZE the primary goal (most important)
      const goalAnswer = questionsAndAnswers.find((qa: QuestionAnswer) => {
        const q = qa.question.toLowerCase();
        const isGoal = (q.includes('main goal') || q.includes('outcome')) && q.includes('right now');
        const isStruggle = q.includes('biggest struggle') || (q.includes('struggle') && q.includes('right now'));
        return isGoal || isStruggle;
      });

      const challengeAnswer = questionsAndAnswers.find((qa: QuestionAnswer) => {
        const q = qa.question.toLowerCase();
        return (q.includes('holding') && q.includes('back')) || q.includes('biggest thing');
      });

      // Build prompt for Phase 1 - quick response with search query
      const phase1Messages: MentorGatewayMessage[] = buildPhase1Messages(onboardingQA);

      // Show placeholder text and progress bars while waiting
      setHasReceivedFirstText(true);
      setIsThinking(false);
      setStreamingText('Analyzing your responses and finding the perfect resources for you...');
      setStreamingCompleted(true); // Set to true so ChatBubble animates in (we don't do character streaming here)
      setShowProgressBars(true);

      console.log('📡 Phase 1: Getting quick response...');
      currentPhase = 'phase1_ai_request';
      
      // PHASE 1: Get quick response with search query (~20-25s)
      // Server uses primary.md prompt and general_mentor
      const phase1Data = await mentorChatService.sendStructuredRequest(
        phase1Messages,
        OnboardingPhase1Schema,
        { responseStyle: 'short', modelPreference: 'fast' }
      );
      
      currentPhase = 'phase1_complete';

      console.log('✅ Phase 1 complete:', {
        text: phase1Data.text.substring(0, 50) + '...',
        searchQuery: phase1Data.searchQuery,
        mainFocus: phase1Data.mainFocusLabel,
        extractedGoals: phase1Data.coreProfile?.goals?.length || 0,
        extractedInterests: phase1Data.coreProfile?.interests?.length || 0
      });

      // Update with actual Phase 1 text
      setStreamingText(phase1Data.text);

      // Save extracted profile and main focus immediately
      console.log('💾 Saving extracted profile from Phase 1:', {
        goals: phase1Data.coreProfile.goals,
        interests: phase1Data.coreProfile.interests,
        challenges: phase1Data.coreProfile.challenges,
        personalityTraits: phase1Data.coreProfile.personalityTraits
      });

      currentPhase = 'saving_phase1_profile';

      const profileSaveResult = await PrivateProfileService.updateCoreProfile(userId, {
        mainFocusCategory: phase1Data.mainFocusCategory as any, // Schema validates enum
        mainFocusLabel: phase1Data.mainFocusLabel,
        primaryGoalText: goalAnswer ? String(goalAnswer.answer) : undefined,
        goals: phase1Data.coreProfile.goals,
        interests: phase1Data.coreProfile.interests,
        challenges: phase1Data.coreProfile.challenges,
        personalityTraits: phase1Data.coreProfile.personalityTraits,
      }, 'onboarding:ai_extracted');

      if (profileSaveResult.success) {
        console.log('✅ Phase 1 profile saved successfully:', {
          goalsCount: phase1Data.coreProfile.goals.length,
          interestsCount: phase1Data.coreProfile.interests.length,
          challengesCount: phase1Data.coreProfile.challenges.length,
          traitsCount: phase1Data.coreProfile.personalityTraits.length
        });
      } else {
        console.error('❌ Failed to save Phase 1 profile:', profileSaveResult.error);
        errorReporting.reportError(new Error(`Failed to save Phase 1 profile: ${profileSaveResult.error}`), {
          userId: auth.currentUser?.uid,
          screen: 'CalculationScreen',
          action: 'savePhase1Profile',
          metadata: {
            questionsAnswered: questionsAndAnswers.length,
          },
        });
      }

      setMainFocusData({
        category: phase1Data.mainFocusCategory,
        label: phase1Data.mainFocusLabel,
        primaryGoal: goalAnswer ? String(goalAnswer.answer) : undefined,
      });

      console.log('📡 Phase 2: Getting recommendations with vector search (FAST)...');
      currentPhase = 'phase2_vector_search';

      // PHASE 2: Direct vector search (~5-10s) - much faster than LLM!
      const phase2Data = await getVectorRecommendations(phase1Data.searchQuery, userAudiences);
      
      currentPhase = 'phase2_complete';

      console.log('✅ Phase 2 complete:', {
        booksCount: phase2Data.recommendedBooks?.length || 0,
        insightsCount: phase2Data.insightPlan?.insights?.length || 0
      });

      // Build final results
      // - Phase 1 text stays visible on screen (don't update streamingText)
      // - Phase 2 personalizedMessage gets saved to chat history
      currentPhase = 'assembling_final_results';
      
      const finalResults = {
        chatResponse: {
          fullText: phase2Data.personalizedMessage, // Use personalizedMessage for chat
          recommendations: {
            personalizedMessage: phase2Data.personalizedMessage,
            recommendedBooks: phase2Data.recommendedBooks || [],
            insightPlan: phase2Data.insightPlan || undefined,
            query: phase1Data.searchQuery,
            date: new Date().toISOString().split('T')[0],
            generatedAt: Math.floor(Date.now() / 1000),
          }
        }
      };

      // Mark as complete (Phase 1 text stays visible on screen)
      setStreamingCompleted(true);
      
      currentPhase = 'updating_user_profile';
      await updateUserProfile(userId, finalResults as any, phase1Data);

      currentPhase = 'complete';
      return true;
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      
      console.error('❌ completeOnboardingTask failed:', {
        phase: currentPhase,
        error: errorObj.message,
        errorName: errorObj.name,
        questionsCount: questionsAndAnswers.length,
        audiences: userAudiences,
      });

      errorReporting.reportError(errorObj, {
        userId,
        screen: 'CalculationScreen',
        action: 'completeOnboardingTask',
        metadata: {
          currentPhase,
          questionsAnswered: questionsAndAnswers.length,
          audiences: userAudiences,
          hasGoal: !!getUserGoal(),
          hasChallenge: !!getUserChallenge(),
          hasStyle: !!getUserStyle(),
          errorMessage: errorObj.message,
          errorName: errorObj.name,
          errorStack: errorObj.stack,
          // Network diagnostics
          onLine: typeof navigator !== 'undefined' ? navigator.onLine : 'unknown',
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
          // Auth state
          isAuthenticated: !!auth.currentUser,
          isAnonymous: auth.currentUser?.isAnonymous,
          authUserId: auth.currentUser?.uid,
        },
      });

      setStreamingText(`Error: ${errorObj.message || 'An unknown error occurred.'}`);
      return false;
    }
  };

  const updateUserProfile = async (
    userId: string,
    results: { chatResponse?: { fullText?: string; recommendations?: DailyRecommendations } },
    phase1Data?: any
  ) => {
    try {
      console.log('📝 Processing onboarding results:', results);
      
      const { chatResponse } = results;
      const recommendations = chatResponse?.recommendations;
      const fullText = chatResponse?.fullText || '';

      // Progress bars already shown when streaming started
      // Now we process the data while animations run

      // Create profile summary from the conversational response
      if (fullText) {
        console.log('💾 Creating profile summary from conversation...');
        const profileSummary: ProfileSummary = {
          shortSummary: [{
            value: fullText.substring(0, 200) + '...',
            label: 'Profile Summary'
          }],
          fullDescription: [{
            title: 'Your Personalized Profile',
            description: fullText,
          }],
          lastUpdated: Date.now() / 1000,
        };
        await PrivateProfileService.updateUserProfileSummary(userId, profileSummary);
      }

      // Profile data already extracted and saved by Phase 1 AI (no manual extraction needed)
      // Just extract displayName from answers if present
      const displayNameAnswer = questionsAndAnswers.find((qa: QuestionAnswer) => {
        const question = qa.question.toLowerCase();
        return question.includes('name');
      });
      const userName = displayNameAnswer ? String(displayNameAnswer.answer).trim() : undefined;

      // Save displayName to profile if we have it
      if (userName) {
        console.log('💾 Saving user displayName:', userName);
        await PrivateProfileService.updateProfile(userId, { displayName: userName });
      }

      // Create and save recommendations if they exist
      if (recommendations) {
        console.log('💾 Saving daily recommendations...');
        
        // Ensure recommendations have today's date and all required fields for dashboard display
        const now = Math.floor(Date.now() / 1000);
        const today = new Date().toISOString().split('T')[0];
        
        const dailyRecommendations: DailyRecommendations = {
          date: today, // Ensure it's today's date for dashboard display
          personalizedMessage: recommendations.personalizedMessage || 'Here are your personalized recommendations based on your onboarding.',
          recommendedBooks: (recommendations.recommendedBooks || []).map(book => ({
            bookId: String(book.bookId), // Convert to string for Firestore
            reason: book.reason || '', // Backend may not return reason - use empty string
            recommendedAt: book.recommendedAt || now,
          })),
          insightPlan: recommendations.insightPlan ? {
            title: recommendations.insightPlan.title,
            insights: (recommendations.insightPlan.insights || []).map(insight => ({
              insightId: String(insight.insightId), // Convert to string for Firestore
              reason: insight.reason || '', // Backend may not return reason - use empty string
              bookId: insight.bookId ? String(insight.bookId) : undefined,
              recommendedAt: insight.recommendedAt || now,
            })),
          } : undefined,
          query: 'Onboarding completion recommendations',
          generatedAt: now,
        };
        
        const saveResult = await PrivateProfileService.updateUserDailyRecommendations(userId, dailyRecommendations);
        if (saveResult.success) {
          console.log('✅ Daily recommendations saved successfully for dashboard display');
        } else {
          console.error('❌ Failed to save daily recommendations:', saveResult.error);
          errorReporting.reportError(new Error(`Failed to save daily recommendations: ${saveResult.error}`), {
            userId,
            screen: 'CalculationScreen',
            action: 'saveDailyRecommendations',
            metadata: {
              booksCount: recommendations.recommendedBooks?.length || 0,
              insightsCount: recommendations.insightPlan?.insights?.length || 0,
            },
          });
        }
      }

      // Save onboarding answers
      const onboardingData: OnboardingAnswersCollection = {
        sessionId: `onboarding_${Date.now()}`,
        completedAt: Math.floor(Date.now() / 1000),
        version: '2.0',
        answers: questionsAndAnswers.map((qa: QuestionAnswer) => ({
          questionText: qa.question,
          answer: Array.isArray(qa.answer) ? qa.answer.join(', ') : String(qa.answer),
        })),
      };

      await PrivateProfileService.updateOnboardingAnswers(userId, onboardingData);

      // Update active context with AI-extracted data from Phase 1 (if provided)
      if (phase1Data?.coreProfile) {
        const contextUpdate: Partial<Omit<ActiveContext, 'lastUpdated' | 'source'>> = {
          currentFocus: phase1Data.coreProfile.goals.length > 0 ? phase1Data.coreProfile.goals : undefined,
          urgentNeeds: phase1Data.coreProfile.challenges.length > 0 ? phase1Data.coreProfile.challenges : undefined,
          information: `Completed onboarding. Interests: ${phase1Data.coreProfile.interests.join(', ')}.`,
        };

        await PrivateProfileService.updateActiveContext(userId, contextUpdate, 'onboarding');
      }

      // Save the onboarding response as a chat message
      // This ensures it appears as the first message when users enter the MentorChatScreen
      if (fullText) {
        console.log('💬 Saving onboarding response as chat message...', {
          fullTextLength: fullText.length,
          hasRecommendations: !!recommendations,
          booksCount: recommendations?.recommendedBooks?.length || 0,
          insightsCount: recommendations?.insightPlan?.insights?.length || 0
        });
        
        const chatTimestamp = Date.now() / 1000;
        
        // Format recommendations with string IDs (Firestore expects strings)
        const formattedRecommendations = recommendations ? {
          books: (recommendations.recommendedBooks || []).map(book => {
            const converted = {
              bookId: String(book.bookId),
              reason: book.reason || '', // Backend may not return reason
              recommendedAt: chatTimestamp
            };
            return converted;
          }),
          insights: (recommendations.insightPlan?.insights || []).map(insight => {
            const converted = {
              insightId: String(insight.insightId),
              reason: insight.reason || '', // Backend may not return reason
              bookId: insight.bookId ? String(insight.bookId) : undefined,
              recommendedAt: chatTimestamp
            };
            return converted;
          }),
        } : undefined;
        
        console.log('🔄 Formatted recommendations for chat:', {
          booksCount: formattedRecommendations?.books?.length,
          firstBookId: formattedRecommendations?.books?.[0]?.bookId,
          firstBookIdType: typeof formattedRecommendations?.books?.[0]?.bookId
        });
        
        const chatEntry: MentorChatHistory = {
          id: `onboarding_${chatTimestamp}`,
          timestamp: chatTimestamp,
          userMessage: "I just completed my onboarding. Can you tell me about my personalized profile and recommendations?",
          assistantResponse: fullText,
          recommendations: formattedRecommendations,
        };

        console.log('📤 Attempting to save chat entry:', {
          id: chatEntry.id,
          messageLength: chatEntry.assistantResponse.length,
          hasBooks: !!(formattedRecommendations?.books?.length),
          hasInsights: !!(formattedRecommendations?.insights?.length)
        });

        const chatResult = await PrivateProfileService.addUserChatHistory(userId, chatEntry);
        if (chatResult.success) {
          console.log('✅ Onboarding response saved to chat history');
        } else {
          console.error('❌ Failed to save onboarding response to chat:', chatResult.error);
          errorReporting.reportError(new Error(`Failed to save onboarding chat: ${chatResult.error}`), {
            userId,
            screen: 'CalculationScreen',
            action: 'saveChatHistory',
            metadata: {
              messageLength: fullText.length,
            },
          });
        }
      } else {
        console.warn('⚠️ No fullText to save to chat history');
      }

      // Create profile data for navigation
      if (fullText) {
        setProfileData({
          profileSummary: [{ value: fullText.substring(0, 200) + '...', label: 'Profile Summary' }],
          fullDescription: [{ title: 'Your Personalized Profile', description: fullText }],
          recommendedBooks: recommendations?.recommendedBooks?.map(rec => ({
            title: rec.bookId,
            author: 'Author',
            reason: rec.reason,
          })) || [],
          actionPlan: recommendations?.insightPlan ? {
            title: recommendations.insightPlan.title,
            steps: recommendations.insightPlan.insights.map((insight, index) => ({
              timeframe: `Step ${index + 1}`,
              action: insight.insightId,
              expectedOutcome: insight.reason,
            })),
          } : {
            title: 'Your Growth Plan',
            steps: [],
          },
        });
      }
    } catch (error) {
      errorReporting.reportError(error, {
        userId: userId,
        screen: 'CalculationScreen',
        action: 'updateUserProfile',
        metadata: {
          hasRecommendations: !!results.chatResponse?.recommendations,
          errorStage: 'profile_update',
        }
      });
      // Re-throw to let calling code handle it
      throw error;
    }
  };

  const animateProgress = () => {
    progressItems.forEach((item, index) => {
      setTimeout(() => {
        // Animate to completion
        const targetValue = 1.0;
        
        // Add listener to update display value
        const listener = item.progress.addListener(({ value }) => {
          setProgressItems(prev => {
            const updated = [...prev];
            updated[index].displayValue = Math.round(value * 100);
            return updated;
          });
        });

        Animated.timing(item.progress, {
          toValue: targetValue,
          duration: 28000,  // 28 seconds per bar - slower to give AI more time
          easing: Easing.out(Easing.cubic), // Natural easing that starts fast, slows down
          useNativeDriver: false,
        }).start(() => {
          // Mark as completed
          setProgressItems(prev => {
            const updated = [...prev];
            updated[index].completed = true;
            updated[index].displayValue = 100;
            return updated;
          });
          
          // Remove listener
          item.progress.removeListener(listener);
          
          // Check if all completed
          if (index === progressItems.length - 1) {
            setAllAnimationCompleted(true);
          }
        });
      }, index * 5500);  // 5.5 second stagger - adds ~5 seconds to total time
    });
  };

  if (error) {
    return (
      <PageContainer>
        <OnboardingPageContainer>
          <ContentContainer>
            <View style={styles.errorContainer}>
            <Text variant="heading3" color="error" align="center">
              {t('calculation.errors.somethingWentWrong')}
            </Text>
            <Text variant="body1" color="secondary" align="center" style={styles.errorMessage}>
              {error}
            </Text>
            <Button
              title={t('calculation.errors.tryAgain')}
              onPress={() => startOnboarding()}
              style={styles.retryButton}
            />
          </View>
        </ContentContainer>
        </OnboardingPageContainer>
      </PageContainer>
    );
  }

  // Show loading indicator during initial mount
  if (isInitializing) {
    return (
      <PageContainer>
        <OnboardingPageContainer>
          <ContentContainer>
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={Theme.colors.primary} />
              <Text variant="body1" color="secondary" align="center" style={styles.loadingText}>
                {t('calculation.preparingExperience')}
              </Text>
            </View>
          </ContentContainer>
        </OnboardingPageContainer>
      </PageContainer>
    );
  }

  // Show "What I Know About You" summary screen when ready
  if (showSummary) {
    const aiProfileSummary = profileData?.profileSummary?.[0]?.value || profileData?.fullDescription?.[0]?.description;
    
    return (
      <PageContainer>
        <OnboardingPageContainer>
          <ContentContainer>
            <ScrollView style={styles.summaryContainer} contentContainerStyle={styles.summaryContent}>
            <Text variant="heading2" align="center" style={styles.summaryTitle}>
              What I Know About You
            </Text>
            <Text variant="body1" color="secondary" align="center" style={styles.summarySubtitle}>
              Here's what I'll use to personalize your experience:
            </Text>

            {/* Main Focus Card - Most prominent */}
            {mainFocusData && (
              <View style={styles.mainFocusSummaryCard}>
                <Text variant="caption" style={styles.summaryMainFocusLabel}>
                  YOUR MAIN FOCUS
                </Text>
                <Text variant="heading3" style={styles.summaryMainFocusTitle}>
                  {mainFocusData.label}
                </Text>
                <Text variant="body2" color="secondary" style={styles.mainFocusDescription}>
                  All your recommendations, books, and daily steps are tailored to this focus area.
                </Text>
              </View>
            )}

            {/* Key profile details - condensed */}
            <View style={styles.summaryItems}>
              {userGoal && (
                <View style={styles.summaryItem}>
                  <Text variant="caption" style={styles.summaryLabel}>Your Goal</Text>
                  <Text variant="body1" style={styles.summaryValue}>{userGoal}...</Text>
                </View>
              )}
              {userChallenge && (
                <View style={styles.summaryItem}>
                  <Text variant="caption" style={styles.summaryLabel}>Main Challenge</Text>
                  <Text variant="body1" style={styles.summaryValue}>{userChallenge}...</Text>
                </View>
              )}
              {userStyle && (
                <View style={styles.summaryItem}>
                  <Text variant="caption" style={styles.summaryLabel}>Preferred Style</Text>
                  <Text variant="body1" style={styles.summaryValue}>{userStyle}</Text>
                </View>
              )}
            </View>

            <Button
              title="Looks good, let's start!"
              onPress={handleContinueToDashboard}
              style={styles.continueButton}
            />
          </ScrollView>
        </ContentContainer>
        </OnboardingPageContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <OnboardingPageContainer>
        <ScrollView 
        ref={scrollViewRef}
        style={styles.scrollContainer}
        contentContainerStyle={styles.scrollContent}
      >
        {/* Header section with context */}
        <View style={styles.headerSection}>
          <Text variant="heading2" align="center" style={styles.headerTitle}>
            Creating Your Personalized Mentor
          </Text>
          <Text variant="body2" color="secondary" align="center" style={styles.headerSubtitle}>
            We’re analyzing your responses to build your path and generate do-first Steps you can apply today — grounded in the best books.
          </Text>
        </View>

        {/* Chat-like mentor section - only show when we have actual content */}
        {streamingText && streamingText.trim().length > 0 && (
          <View style={styles.chatSection}>
            <View style={styles.messageContainer}>
              <View style={styles.avatarContainer}>
                <ChatAvatar type="mentor" size="large" />
              </View>
              
              <View style={styles.bubbleContainer}>
                <ChatBubble
                  role="assistant"
                  content={streamingText}
                  timestamp={Date.now() / 1000}
                  isStreaming={!streamingCompleted}
                />
              </View>
            </View>
          </View>
        )}

        {/* Show progress bars below when ready */}
        {showProgressBars && (
          <View style={styles.progressSection}>
            {progressItems.map((item, index) => (
              <View key={index}>
                <View style={styles.progressHeader}>
                  <Text variant="body1" color="secondary" align="center">
                    {item.title}
                  </Text>
                  <Text variant="caption" color="secondary" align="center" style={styles.progressSubtitle}>
                    {item.displayValue || 0}%
                  </Text>
                </View>
                
                <View style={styles.progressBarContainer}>
                  <Animated.View
                    style={[
                      styles.progressBar,
                      {
                        backgroundColor: item.color,
                        width: item.progress.interpolate({
                          inputRange: [0, 1],
                          outputRange: ['0%', '100%'],
                        }),
                      },
                    ]}
                  />
                </View>
              </View>
            ))}
            
            {/* Show "Finalizing..." when all bars are done but still waiting for AI */}
            {allAnimationCompleted && !streamingCompleted && (
              <View style={styles.finalizingContainer}>
                <ActivityIndicator size="small" color={Theme.colors.primary500} />
                <Text variant="body1" color="secondary" align="center" style={styles.finalizingText}>
                  Finalizing your personalized plan...
                </Text>
              </View>
            )}
          </View>
        )}
      </ScrollView>
      </OnboardingPageContainer>
    </PageContainer>
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: Theme.spacing.xl,
    gap: Theme.spacing.lg,
    backgroundColor: Theme.colors.background,
  },
  loadingText: {
    marginTop: Theme.spacing.md,
    color: Theme.colors.textSecondary,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: Theme.spacing.xl,
    backgroundColor: Theme.colors.background,
  },
  errorMessage: {
    marginTop: Theme.spacing.md,
    marginBottom: Theme.spacing.xl,
  },
  retryButton: {
    minWidth: 120,
  },
  scrollContainer: {
    flex: 1,
    backgroundColor: Theme.colors.background,
  },
  scrollContent: {
    flexGrow: 1,
  },
  headerSection: {
    paddingHorizontal: Theme.spacing.xl,
    paddingTop: Theme.spacing.xxl,
    paddingBottom: Theme.spacing.lg,
    backgroundColor: Theme.colors.warm50, // Warm background for header
    gap: Theme.spacing.md,
    marginBottom: Theme.spacing.lg,
  },
  headerTitle: {
    marginBottom: Theme.spacing.xs,
    color: Theme.colors.textPrimary,
  },
  headerSubtitle: {
    paddingHorizontal: Theme.spacing.sm,
    color: Theme.colors.textSecondary,
  },
  chatSection: {
    paddingTop: Theme.spacing.lg,
    backgroundColor: Theme.colors.background,
    paddingHorizontal: Theme.spacing.lg,
  },
  messageContainer: {
    flexDirection: 'row',
    paddingHorizontal: Theme.spacing.lg,
    paddingVertical: Theme.spacing.lg,
    gap: Theme.spacing.md,
    backgroundColor: '#E8F9F3', // Mint green like dashboard
    borderRadius: Theme.borderRadius.lg,
    ...Theme.shadows.md,
  },
  avatarContainer: {
    alignSelf: 'flex-start',
    marginTop: Theme.spacing.xs,
  },
  bubbleContainer: {
    flex: 1,
  },
  progressSection: {
    paddingHorizontal: Theme.spacing.xl,
    paddingTop: Theme.spacing.xxl,
    paddingBottom: Theme.spacing.xxxl,
    backgroundColor: Theme.colors.background,
    gap: Theme.spacing.xl,
  },
  progressHeader: {
    gap: Theme.spacing.xs,
    marginBottom: Theme.spacing.sm,
  },
  progressSubtitle: {
    fontSize: 13,
    fontWeight: '600',
    color: Theme.colors.primary,
  },
  progressBarContainer: {
    height: 12,
    backgroundColor: Theme.colors.warm50, // Warm cream background
    borderRadius: Theme.borderRadius.md,
    overflow: 'hidden',
    marginBottom: Theme.spacing.lg,
    ...Theme.shadows.sm,
  },
  progressBar: {
    height: '100%',
    borderRadius: Theme.borderRadius.md,
  },
  finalizingContainer: {
    marginTop: Theme.spacing.xl,
    paddingVertical: Theme.spacing.lg,
    alignItems: 'center',
    gap: Theme.spacing.md,
  },
  finalizingText: {
    fontSize: 14,
    fontWeight: '500',
  },
  summaryContainer: {
    flex: 1,
    backgroundColor: Theme.colors.background,
  },
  summaryContent: {
    padding: Theme.spacing.xl,
    gap: Theme.spacing.lg,
  },
  summaryTitle: {
    marginTop: Theme.spacing.md,
    color: Theme.colors.textPrimary,
  },
  summarySubtitle: {
    marginBottom: Theme.spacing.lg,
    color: Theme.colors.textSecondary,
  },
  mainFocusSummaryCard: {
    backgroundColor: '#E8F9F3', // Mint green like MentorHero
    padding: Theme.spacing.xl,
    borderRadius: Theme.borderRadius.lg,
    marginBottom: Theme.spacing.lg,
    ...Theme.shadows.md,
  },
  summaryMainFocusLabel: {
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    fontWeight: '700',
    color: Theme.colors.primary,
    marginBottom: Theme.spacing.sm,
    fontSize: 11,
  },
  summaryMainFocusTitle: {
    marginBottom: Theme.spacing.md,
    color: Theme.colors.primary,
    fontSize: 26,
  },
  mainFocusDescription: {
    lineHeight: 22,
    color: Theme.colors.textSecondary,
  },
  aiSummaryCard: {
    backgroundColor: Theme.colors.warm50, // Warm cream background
    padding: Theme.spacing.xl,
    borderRadius: Theme.borderRadius.lg,
    marginBottom: Theme.spacing.lg,
    ...Theme.shadows.sm,
  },
  aiSummaryText: {
    color: Theme.colors.textSecondary,
    lineHeight: 24,
    fontSize: 15,
  },
  summaryItems: {
    gap: Theme.spacing.md,
    paddingVertical: Theme.spacing.sm,
  },
  summaryItem: {
    backgroundColor: Theme.colors.surface,
    padding: Theme.spacing.lg,
    borderRadius: Theme.borderRadius.md,
    gap: Theme.spacing.sm,
    ...Theme.shadows.sm,
  },
  summaryLabel: {
    color: Theme.colors.primary,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    fontSize: 11,
  },
  summaryValue: {
    color: Theme.colors.textPrimary,
    fontSize: 15,
    lineHeight: 22,
  },
  continueButton: {
    marginTop: Theme.spacing.xl,
    marginBottom: Theme.spacing.md,
  },
});

export default CalculationScreen; 